import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const dealSchema = z.object({
  title: z.string().min(1, "Title is required").max(200),
  value: z.string().optional(),
  expected_close_date: z.string().optional(),
  contact_id: z.string().optional(),
  pipeline_stage_id: z.string().min(1, "Stage is required"),
  notes: z.string().max(1000).optional(),
});

type DealFormData = z.infer<typeof dealSchema>;

interface Contact {
  id: string;
  name: string;
}

interface Stage {
  id: string;
  name: string;
}

interface DealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deal: any | null;
  stages: Stage[];
  onSuccess: () => void;
}

export function DealDialog({
  open,
  onOpenChange,
  deal,
  stages,
  onSuccess,
}: DealDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);

  const form = useForm<DealFormData>({
    resolver: zodResolver(dealSchema),
    defaultValues: {
      title: "",
      value: "",
      expected_close_date: "",
      contact_id: "",
      pipeline_stage_id: "",
      notes: "",
    },
  });

  useEffect(() => {
    if (open) {
      fetchContacts();
    }
  }, [open]);

  useEffect(() => {
    if (deal?.id) {
      form.reset({
        title: deal.title || "",
        value: deal.value?.toString() || "",
        expected_close_date: deal.expected_close_date || "",
        contact_id: deal.contact_id || "",
        pipeline_stage_id: deal.pipeline_stage_id || stages[0]?.id || "",
        notes: deal.notes || "",
      });
    } else {
      // For new deals
      const defaultStageId = deal?.pipeline_stage_id || stages[0]?.id || "";
      form.reset({
        title: "",
        value: "",
        expected_close_date: "",
        contact_id: "",
        pipeline_stage_id: defaultStageId,
        notes: "",
      });
    }
  }, [deal, stages, open]);

  const fetchContacts = async () => {
    try {
      const { data, error } = await supabase
        .from("contacts")
        .select("id, name")
        .order("name");

      if (error) throw error;
      setContacts(data || []);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const onSubmit = async (data: DealFormData) => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const dealData = {
        title: data.title,
        value: data.value ? parseFloat(data.value) : null,
        expected_close_date: data.expected_close_date || null,
        contact_id: data.contact_id || null,
        pipeline_stage_id: data.pipeline_stage_id,
        notes: data.notes || null,
        user_id: user.id,
      };

      if (deal?.id) {
        const { error } = await supabase
          .from("deals")
          .update(dealData)
          .eq("id", deal.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Deal updated successfully",
        });
      } else {
        const { error } = await supabase.from("deals").insert([dealData]);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Deal created successfully",
        });
      }

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving deal:", error);
      toast({
        title: "Error",
        description: "Failed to save deal",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deal?.id || !confirm("Are you sure you want to delete this deal?")) return;

    try {
      const { error } = await supabase.from("deals").delete().eq("id", deal.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Deal deleted successfully",
      });

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Error deleting deal:", error);
      toast({
        title: "Error",
        description: "Failed to delete deal",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{deal?.id ? "Edit Deal" : "Add Deal"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Deal Title *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="New Enterprise Deal" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Deal Value ($)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        placeholder="50000"
                        step="0.01"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expected_close_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expected Close Date</FormLabel>
                    <FormControl>
                      <Input {...field} type="date" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="contact_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || undefined}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select contact (optional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {contacts.map((contact) => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.name}
                        </SelectItem>
                      ))}
                      {contacts.length === 0 && (
                        <div className="p-2 text-sm text-muted-foreground">
                          No contacts available
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="pipeline_stage_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pipeline Stage *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select stage" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {stages.map((stage) => (
                        <SelectItem key={stage.id} value={stage.id}>
                          {stage.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Additional notes about this deal..."
                      rows={4}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-between gap-2">
              <div>
                {deal?.id && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? "Saving..." : deal?.id ? "Update" : "Create"}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
